import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import AddToWorkspaceDialog from './AddToWorkspaceDialog';
import useWorkspaces from '../../hooks/useWorkspaces';
import { WORKSPACES_CHANGED, announceWorkspacesChanged, getProjectWorkspaces } from '../../services/WorkspacesService';
import './ProjectWorkspaceChips.css';

// One answer per project for a few seconds: the header and Home ask together.
const cache = new Map(); // projectId -> { at, promise }
const FRESH_MS = 10000;
if (typeof window !== 'undefined') {
  window.addEventListener(WORKSPACES_CHANGED, (event) => cache.delete(event.detail?.projectId));
}
function workspacesOfProject(projectId) {
  const hit = cache.get(projectId);
  if (hit && Date.now() - hit.at < FRESH_MS) { return hit.promise; }
  const promise = getProjectWorkspaces(projectId).catch(() => null);
  cache.set(projectId, { at: Date.now(), promise });
  return promise;
}

/** A project's workspaces (`null` while loading or where the gateway has none), reloaded when they change. */
export function useProjectWorkspaces(projectId, { enabled = true } = {}) {
  const [workspaces, setWorkspaces] = useState(null);
  const load = useCallback(() => {
    if (!enabled || !projectId) { setWorkspaces(null); return () => {}; }
    let alive = true;
    workspacesOfProject(projectId).then((list) => { if (alive) { setWorkspaces(list); } });
    return () => { alive = false; };
  }, [enabled, projectId]);
  useEffect(() => load(), [load]);
  useEffect(() => {
    const onChange = (event) => {
      if (event.detail?.projectId === projectId) { load(); }
    };
    window.addEventListener(WORKSPACES_CHANGED, onChange);
    return () => window.removeEventListener(WORKSPACES_CHANGED, onChange);
  }, [projectId, load]);
  return workspaces;
}

function WorkspaceChip({ workspace }) {
  const inRouter = useInRouterContext();
  return inRouter ? <RoutedChip workspace={workspace} /> : (
    <a className="sbx-pill project-workspace-tag" data-testid="project-workspace" href={`/workspaces/${encodeURIComponent(workspace.id)}`} role="listitem" title={`In the workspace ${workspace.name}`}>{workspace.name}</a>
  );
}
function RoutedChip({ workspace }) {
  const navigate = useNavigate();
  const href = `/workspaces/${encodeURIComponent(workspace.id)}`;
  return (
    <a
      className="sbx-pill project-workspace-tag"
      data-testid="project-workspace"
      href={href}
      onClick={(event) => { event.preventDefault(); navigate(href); }}
      role="listitem"
      title={`In the workspace ${workspace.name}: open it`}
    >
      {workspace.name}
    </a>
  );
}
WorkspaceChip.propTypes = { workspace: PropTypes.object.isRequired };
RoutedChip.propTypes = WorkspaceChip.propTypes;

/**
 * A project's workspaces as chips (the project header, its Home), each opening the workspace; the person sees the ones
 * they are in, the project's owner all of them. `manage` (the owner): "Add to workspace…" (AddToWorkspaceDialog), also
 * when it is in none. Nothing where the gateway has no workspaces.
 */
export default function ProjectWorkspaceChips({ projectId, projectName = '', manage = false, enabled = true }) {
  const workspaces = useProjectWorkspaces(projectId, { enabled });
  const [picking, setPicking] = useState(false);
  const mine = useWorkspaces({ enabled: picking });
  if (!workspaces || (workspaces.length === 0 && !manage)) { return null; }
  return (
    <span className="project-workspace-chips is-header" data-testid="project-workspaces">
      {workspaces.length > 0 && (
        <span aria-label={`Workspaces: ${workspaces.map((workspace) => workspace.name).join(', ')}`} className="project-workspace-chips" role="list">
          {workspaces.map((workspace) => <WorkspaceChip key={workspace.id} workspace={workspace} />)}
        </span>
      )}
      {manage && (
        <button className="ph-link ph-link-inline project-workspace-add" onClick={() => setPicking(true)} type="button">
          {workspaces.length ? 'Workspaces…' : 'Add to workspace…'}
        </button>
      )}
      {manage && picking && (
        <AddToWorkspaceDialog
          current={workspaces}
          loading={mine.loading}
          onClose={() => setPicking(false)}
          onSaved={({ keepOpen }) => {
            if (!keepOpen) { setPicking(false); }
            announceWorkspacesChanged(projectId);
            mine.reload();
          }}
          open
          project={{ id: projectId, name: projectName }}
          workspaces={mine.workspaces}
        />
      )}
    </span>
  );
}

ProjectWorkspaceChips.propTypes = {
  enabled: PropTypes.bool,
  manage: PropTypes.bool,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
};
